import '../i18n/autoTranslate';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SessionProgressIndicator from '../../components/ui/SessionProgressIndicator';
import InterviewVideoInterface from './components/InterviewVideoInterface';
import QuestionDisplay from './components/QuestionDisplay';
import AnswerInputSection from './components/AnswerInputSection';
import RealTimeFeedback from './components/RealTimeFeedback';
import SessionControlPanel from './components/SessionControlPanel';
import NotesPanel from './components/NotesPanel';
import Icon from '../../components/AppIcon';
import SpotlightCard from '../../components/ui/SpotlightCard';
import AppBackground from '../../components/ui/AppBackground.tsx';


import { INTERVIEW_API_BASE_URL, getAccessToken, getAuthHeaders } from '../../config/api';

const LiveInterviewSession = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Load configuration from localStorage or location state
  const [sessionConfig, setSessionConfig] = useState(null);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  // Session state
  const [sessionState, setSessionState] = useState({
    isActive: true,
    isPaused: false,
    isRecording: false,
    currentQuestion: 1,
    totalQuestions: 10,
    timeRemaining: 1800, // 30 minutes
    sessionMode: 'video', // video, audio, text, technical
    sessionType: 'mock' // practice, mock, live
  });

  // UI state
  const [showNotes, setShowNotes] = useState(false);
  const [showFeedback, setShowFeedback] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // Mock session data from setup
  // const [sessionConfig] = useState({
  //   jobRole: 'Senior Software Engineer',
  //   company: 'TechCorp',
  //   interviewType: 'Technical + Behavioral',
  //   difficulty: 'Advanced',
  //   duration: 30,
  //   aiPersonality: 'Professional',
  //   sessionId: 'session_' + Date.now()
  // });

  // Mock questions data
  const [questions, setQuestions] = useState([]);
  const [collectedAnswers, setCollectedAnswers] = useState([]);
  
  // Load configuration on mount
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        // Try to get from localStorage first (saved from InterviewSetupConfiguration)
        const savedConfig = localStorage.getItem('finalInterviewConfiguration');
        
        if (savedConfig) {
          const config = JSON.parse(savedConfig);
          setSessionConfig(config);
          setCollectedAnswers([]);
          
          // Update session state based on config
          setSessionState(prev => ({
            ...prev,
            sessionMode: config.selectedFormat,
            timeRemaining: config.duration * 60, // Convert minutes to seconds
            totalQuestions: config.estimatedQuestions
          }));

          const token = getAccessToken();
          let generatedQuestions = null;

          if (token) {
            try {
              const difficultyLevel = Number(config?.personality?.difficulty || 3);
              const difficulty =
                difficultyLevel >= 4 ? 'advanced' : difficultyLevel <= 2 ? 'beginner' : 'intermediate';

              const r = await fetch(`${INTERVIEW_API_BASE_URL}/generate-questions`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                  questionCount: Math.min(Number(config?.estimatedQuestions || 5), 10),
                  interviewType: config?.selectedType || 'behavioral',
                  jobRole: config?.selectedRole || 'General',
                  industry: config?.selectedIndustry || 'technology',
                  difficulty,
                  personaStyle: config?.personality?.selectedPersona?.description || 'professional',
                  tone: config?.personality?.tone || 3,
                  difficulty_level: config?.personality?.difficulty || 3,
                  starMethodEnforcement: config?.starMethodEnforcement || false,
                  enableFollowUps: config?.enableFollowUps || false
                })
              });

              if (r.ok) {
                const data = await r.json();
                const rawItems = Array.isArray(data?.question_items) && data.question_items.length
                  ? data.question_items
                  : (Array.isArray(data?.questions) ? data.questions : []);

                generatedQuestions = rawItems.map((item, idx) => {
                  const qText = typeof item === 'object' ? item?.question : item;
                  return {
                    id: item?.id || idx + 1,
                    type: config?.selectedType || 'behavioral',
                    category: 'AI',
                    difficulty,
                    question: String(qText || ''),
                    audio_url: item?.audio_url || item?.audioUrl || '',
                    audio_status: item?.audio_status || '',
                    audio_provider: item?.audio_provider || 'elevenlabs',
                    hint: config?.starMethodEnforcement
                      ? 'Remember to use the STAR method in your response.'
                      : 'Take your time to provide a thorough answer.',
                    timeLimit: 180
                  };
                });
              }
            } catch (e) {
              generatedQuestions = null;
            }
          }

          if (!generatedQuestions || !generatedQuestions.length) {
            generatedQuestions = generateQuestionsFromConfig(config);
          }

          setQuestions(generatedQuestions);
          
          setIsConfigLoaded(true);
          const interviewId = localStorage.getItem('current_interview_id');
          if (interviewId) {
            if (token) {
              fetch(`${INTERVIEW_API_BASE_URL}/${interviewId}/status`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status: 'in_progress' })
              }).catch(() => {});
            }
          }
        } else {
          // If no config found, redirect back to setup
          console.error('No interview configuration found');
          navigate('/interview-setup-configuration');
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
        navigate('/interview-setup-configuration');
      }
    };

    loadConfiguration();
  }, [navigate]);

    // Generate questions based on configuration
  const generateQuestionsFromConfig = (config) => {
    const questionPool = [];
    
    // Add questions based on selected type and categories
    const questionTypes = {
      behavioral: [
        {
          type: 'behavioral',
          category: 'Leadership',
          difficulty: 'intermediate',
          question: `Tell me about a time when you had to lead a team through a challenging project. How did you handle conflicts and ensure the project's success?`,
          hint: `Use the STAR method: Situation, Task, Action, Result. Focus on specific examples and quantifiable outcomes.`,
          timeLimit: 180
        },
        {
          type: 'behavioral',
          category: 'Problem Solving',
          difficulty: 'intermediate',
          question: `Describe a situation where you had to solve a complex problem under tight deadlines. What was your approach?`,
          hint: `Focus on your problem-solving methodology and how you prioritized tasks.`,
          timeLimit: 180
        },
        {
          type: 'behavioral',
          category: 'Teamwork',
          difficulty: 'intermediate',
          question: `Tell me about a time when you had to work with a difficult team member. How did you handle the situation?`,
          hint: `Demonstrate your interpersonal skills and conflict resolution abilities.`,
          timeLimit: 180
        }
      ],
      technical: [
        {
          type: 'technical',
          category: 'System Design',
          difficulty: 'hard',
          question: `Design a scalable system for handling real-time notifications for a social media platform with 100 million users. Consider both push notifications and in-app notifications.`,
          hint: `Think about message queues, database design, caching strategies, and load balancing.`,
          timeLimit: 300
        },
        {
          type: 'technical',
          category: 'Algorithms',
          difficulty: 'hard',
          question: `Implement a function to find the longest palindromic substring in a given string. Optimize for both time and space complexity.`,
          hint: `Consider dynamic programming or expand-around-center approaches.`,
          timeLimit: 240
        },
        {
          type: 'technical',
          category: 'Data Structures',
          difficulty: 'medium',
          question: `Explain how you would implement a LRU (Least Recently Used) cache. What data structures would you use and why?`,
          hint: `Think about HashMap and Doubly Linked List combination.`,
          timeLimit: 180
        }
      ],
      case_study: [
        {
          type: 'case_study',
          category: 'Business Analysis',
          difficulty: 'hard',
          question: `A retail company is experiencing declining sales despite increased foot traffic. How would you analyze this situation and what recommendations would you make?`,
          hint: `Consider metrics, customer behavior, competition, and operational efficiency.`,
          timeLimit: 300
        }
      ],
      stress: [
        {
          type: 'stress',
          category: 'Pressure Handling',
          difficulty: 'hard',
          question: `You have 2 hours to fix a critical production bug affecting millions of users. Walk me through your immediate actions.`,
          hint: `Demonstrate your ability to stay calm and prioritize under extreme pressure.`,
          timeLimit: 120
        }
      ],
      panel: [
        {
          type: 'panel',
          category: 'Mixed',
          difficulty: 'hard',
          question: `Multiple interviewers are asking: How do you balance technical excellence with business deadlines?`,
          hint: `Address both technical and business perspectives in your answer.`,
          timeLimit: 180
        }
      ]
    };

    // Select questions based on config
    const selectedType = config.selectedType;
    const availableQuestions = questionTypes[selectedType] || questionTypes.behavioral;
    
    // Add custom questions if any
    if (config.customQuestions && config.customQuestions.length > 0) {
      config.customQuestions.forEach((customQ, index) => {
        questionPool.push({
          id: `custom_${index}`,
          type: selectedType,
          category: 'Custom',
          difficulty: 'medium',
          question: customQ,
          hint: config.starMethodEnforcement ? 'Remember to use the STAR method in your response.' : 'Take your time to provide a thorough answer.',
          timeLimit: 180
        });
      });
    }

    // Add questions from the pool
    availableQuestions.forEach((q, index) => {
      questionPool.push({
        ...q,
        id: index + 1
      });
    });

    // If surprise questions are enabled, add some unexpected ones
    if (config.surpriseQuestions) {
      questionPool.push({
        id: questionPool.length + 1,
        type: 'surprise',
        category: 'Critical Thinking',
        difficulty: 'hard',
        question: `If you were an animal, which one would you be and why? Relate it to your professional qualities.`,
        hint: `Be creative but professional. Show self-awareness.`,
        timeLimit: 120
      });
    }

    return questionPool.slice(0, config.estimatedQuestions);
  };
  
  const currentQuestion = questions?.[sessionState?.currentQuestion - 1];

  // Timer effect
  useEffect(() => {
    let interval;
    if (sessionState?.isActive && !sessionState?.isPaused && sessionState?.timeRemaining > 0) {
      interval = setInterval(() => {
        setSessionState(prev => ({
          ...prev,
          timeRemaining: prev?.timeRemaining - 1
        }));
      }, 1000);
    }
  
    if (sessionState?.timeRemaining <= 0) {
      handleEndSession();
    }
  
    return () => clearInterval(interval);
  }, [sessionState?.isActive, sessionState?.isPaused, sessionState?.timeRemaining]);

  // Session control handlers
  const handlePauseSession = () => {
    setSessionState(prev => ({ ...prev, isPaused: true }));
  };

  const handleResumeSession = () => {
    setSessionState(prev => ({ ...prev, isPaused: false }));
  };

  const handleToggleRecording = () => {
    setSessionState(prev => ({ ...prev, isRecording: !prev?.isRecording }));
  };

  const handleSkipQuestion = () => {
    if (sessionState?.currentQuestion < sessionState?.totalQuestions) {
      setSessionState(prev => ({
        ...prev,
        currentQuestion: prev?.currentQuestion + 1,
        isRecording: false
      }));
    }
  };

  const handleNextQuestion = () => {
    if (sessionState?.currentQuestion < sessionState?.totalQuestions) {
      setIsLoading(true);
      setTimeout(() => {
        setSessionState(prev => ({
          ...prev,
          currentQuestion: prev?.currentQuestion + 1,
          isRecording: false
        }));
        setIsLoading(false);
      }, 1500);
    } else {
      handleEndSession();
    }
  };

  const handleSubmitAnswer = (answer) => {
    console.log('Answer submitted:', answer);
    const currentQ = questions?.[sessionState?.currentQuestion - 1] || null;
    const questionText = currentQ?.question || '';

    let transcriptText = '';
    if (answer?.mode === 'text') {
      transcriptText = typeof answer?.content === 'string' ? answer.content : '';
    } else if (answer?.mode === 'technical') {
      transcriptText = typeof answer?.content?.text === 'string' ? answer.content.text : '';
    }

    const durationSeconds = typeof answer?.duration === 'number' ? answer.duration : 0;
    const token = getAccessToken();

    if (token && transcriptText.trim()) {
      fetch(`${INTERVIEW_API_BASE_URL}/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          text: transcriptText,
          question: questionText,
          duration: Math.max(1, durationSeconds || 1),
          delay: 2,
          use_ai: true
        })
      })
        .then(r => (r.ok ? r.json() : null))
        .then(analysis => {
          setCollectedAnswers(prev => [
            ...prev,
            {
              question: questionText,
              transcript: transcriptText,
              duration: Math.max(1, durationSeconds || 1),
              analysis
            }
          ]);
        })
        .catch(() => {
          setCollectedAnswers(prev => [
            ...prev,
            {
              question: questionText,
              transcript: transcriptText,
              duration: Math.max(1, durationSeconds || 1),
              analysis: null
            }
          ]);
        });
    } else {
      setCollectedAnswers(prev => [
        ...prev,
        {
          question: questionText,
          transcript: transcriptText,
          duration: Math.max(1, durationSeconds || 1),
          analysis: null
        }
      ]);
    }
    // Simulate processing time
    setIsLoading(true);
    setTimeout(() => {
      handleNextQuestion();
    }, 2000);
  };

  const handleEndSession = async () => {
    setSessionState(prev => ({ ...prev, isActive: false }));
    
    // Prepare session data for results page
    const sessionResults = {
      ...sessionConfig,
      sessionId: `INT-${Date.now()}`,
      questionsAnswered: sessionState?.currentQuestion,
      totalQuestions: sessionState?.totalQuestions,
      timeSpent: (sessionConfig?.duration * 60) - sessionState?.timeRemaining,
      completionRate: (sessionState?.currentQuestion / sessionState?.totalQuestions) * 100,
      completedAt: new Date()
    };

    const interviewId = localStorage.getItem('current_interview_id');
    const token = getAccessToken();
    if (interviewId && token) {
      try {
        const answersPayload = collectedAnswers
          .filter(a => a?.question)
          .map(a => {
            const m = a?.analysis?.local_analysis?.metrics || {};
            return {
              question: a.question,
              transcript: a.transcript || '',
              duration: a.duration || 30,
              metrics: {
                clarity: m.clarity || 0,
                pacing: m.pacing || 0,
                response_time: m.response_time || 85,
                professionalism: m.professionalism || 0,
                tone: m.tone || 0
              }
            };
          });

        const fillerWords = collectedAnswers.reduce((acc, a) => {
          const fw = a?.analysis?.local_analysis?.filler_words || {};
          Object.entries(fw).forEach(([k, v]) => {
            acc[k] = (acc[k] || 0) + (typeof v === 'number' ? v : 0);
          });
          return acc;
        }, {});

        let finalAnalysis = null;
        if (answersPayload.length) {
          const r = await fetch(`${INTERVIEW_API_BASE_URL}/final-analysis`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              answers: answersPayload,
              fillerWords,
              use_ai: true
            })
          });
          if (r.ok) {
            finalAnalysis = await r.json();
          }
        }

        const finalScore = Math.round(
          finalAnalysis?.final_score ??
            finalAnalysis?.ai_analysis?.final_score ??
            finalAnalysis?.local_analysis?.final_score ??
            sessionResults.completionRate ??
            0
        );

        const localInsights = finalAnalysis?.local_analysis?.insights || {};
        const ai = finalAnalysis?.ai_analysis || null;

        const strengths = ai?.detailed_strengths?.map(s => (typeof s === 'string' ? s : s?.text)).filter(Boolean) || localInsights.strengths || [];
        const weakestAreas = ai?.detailed_weaknesses?.map(w => (typeof w === 'string' ? w : w?.text)).filter(Boolean) || localInsights.areas_for_improvement || [];
        const recommendations = ai?.actionable_recommendations || localInsights.recommendations || [];

        const answerReviews = collectedAnswers.map((a, idx) => {
          const perScore = a?.analysis?.final_score ?? a?.analysis?.local_analysis?.overall_score ?? null;
          const perInsights = a?.analysis?.local_analysis?.insights || {};
          return {
            id: idx + 1,
            question: a.question,
            user_answer: a.transcript || '',
            ai_model_answer: '',
            score: perScore === null ? 0 : Math.round(perScore),
            highlights: {
              strengths: perInsights.strengths || [],
              improvements: perInsights.areas_for_improvement || []
            },
            duration: `${Math.round((a.duration || 0) / 60)}:${String(Math.round((a.duration || 0) % 60)).padStart(2, '0')}`
          };
        });

        const average = finalAnalysis?.local_analysis?.statistics?.average_metrics || {};
        const performanceCategories = {
          communication: {
            score: Math.round(average.clarity || 0),
            max_score: 100,
            description: 'Clarity, structure, and articulation'
          },
          confidence: {
            score: Math.round(average.tone || 0),
            max_score: 100,
            description: 'Tone and presence throughout the session'
          },
          problem_solving: {
            score: Math.round(average.professionalism || 0),
            max_score: 100,
            description: 'Professional framing and reasoning'
          },
          technical_skills: {
            score: Math.round(average.pacing || 0),
            max_score: 100,
            description: 'Pacing and delivery under time constraints'
          }
        };

        const detailedFeedback = [
          {
            id: 'overall',
            title: 'Overall Assessment',
            icon: 'MessageSquare',
            score: finalScore,
            insights: [
              {
                type: 'neutral',
                title: 'Summary',
                description: ai?.overall_assessment || '',
                details: ''
              }
            ].filter(i => i.description)
          }
        ];

        const improvementSuggestions = Array.isArray(recommendations)
          ? recommendations.slice(0, 10).map((rec, i) => ({
              id: i + 1,
              category: 'General',
              priority: i < 3 ? 'high' : 'medium',
              title: rec?.action || rec?.title || `Suggestion ${i + 1}`,
              description: rec?.reason || rec?.description || '',
              action_items: rec?.steps || rec?.action_items || [],
              resources: rec?.resources || [],
              estimated_time: rec?.estimated_time || '',
              difficulty: rec?.difficulty || 'Medium'
            }))
          : [];

        const nextSteps = ai?.next_steps
          ? { recommended_practice: ai.next_steps, next_milestone: '', milestones: [] }
          : { recommended_practice: [], next_milestone: '', milestones: [] };

        await fetch(`${INTERVIEW_API_BASE_URL}/${interviewId}/results`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            overall_score: finalScore,
            performance_score: finalScore,
            feedback: ai?.overall_assessment || 'Session completed',
            performance_categories: performanceCategories,
            strengths,
            weakest_areas: weakestAreas,
            detailed_feedback: detailedFeedback,
            answer_reviews: answerReviews,
            improvement_suggestions: improvementSuggestions,
            next_steps: nextSteps,
            speaking_pace_wpm: Math.round(finalAnalysis?.local_analysis?.statistics?.average_words_per_minute || 0),
            filler_word_count: Math.round(finalAnalysis?.local_analysis?.statistics?.total_filler_words || 0),
            total_questions_asked: sessionResults.questionsAnswered || 0
          })
        });
      } catch (e) {}
    }
    
    // Save to localStorage for results page
    localStorage.setItem('lastInterviewSession', JSON.stringify(sessionResults));
    
    navigate('/interview-results-feedback', {
      state: { sessionData: sessionResults }
    });
  };

  const handleTextToSpeech = (text) => {
    console.log('Text to speech:', text);
    // In real implementation, this would use Web Speech API
  };

  // Loading transition between questions
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B12] text-white">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 right-4 w-32 h-32 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full blur-3xl" />
          <div className="absolute bottom-4 left-4 w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full blur-2xl" />
        </div>
       
        {/* <Header /> */}
        <div className="pt-32 pb-24 px-4">
          <SessionProgressIndicator
            isVisible={true}
            currentQuestion={sessionState?.currentQuestion}
            totalQuestions={sessionState?.totalQuestions}
            timeRemaining={sessionState?.timeRemaining}
            sessionType={sessionState?.sessionType}
            onPause={handlePauseSession}
            onStop={handleEndSession}
          />
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center min-h-[60vh]">
              <SpotlightCard className="w-full max-w-md" spotlightColor="rgba(139, 92, 246, 0.2)">
                <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10 p-8 text-center space-y-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center animate-spin">
                    <Icon name="Loader2" size={32} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Processing Your Answer</h2>
                    <p className="text-gray-300">Analyzing response and preparing next question...</p>
                  </div>
                  <div className="w-64 h-2 bg-white/20 rounded-full mx-auto overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full animate-pulse" style={{ width: '70%' }} />
                  </div>
                </div>
              </SpotlightCard>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen h-full   text-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-4 right-4 w-32 h-32 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full blur-3xl" />
        <div className="absolute bottom-4 left-4 w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full blur-2xl" />
      </div>

      
      {/* <Header /> */}
      {/* Session Progress */}
      <SessionProgressIndicator
        isVisible={sessionState?.isActive}
        currentQuestion={sessionState?.currentQuestion}
        totalQuestions={sessionState?.totalQuestions}
        timeRemaining={sessionState?.timeRemaining}
        sessionType={sessionState?.sessionType}
        onPause={handlePauseSession}
        onStop={handleEndSession}
      />
      
      {/* Main Interview Interface */}
      <main className="relative pt-16 pb-48 px-4 bg-[#0B0B12] ">
        <AppBackground>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 h-full ">

            {/* Left Column - Video Interface */}
            <div className="lg:col-span-2 space-y-6">
              {/* Video/Audio Interface */}
              {/* <div className="h-64 md:h-80 lg:h-96"> */}
                <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
                  <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10 rounded-lg overflow-hidden h-full">
                    <InterviewVideoInterface
                      isRecording={sessionState?.isRecording}
                      onToggleRecording={handleToggleRecording}
                      sessionMode={sessionState?.sessionMode}
                      aiInterviewer={sessionConfig?.personality?.selectedPersona}
                    />
                  </div>
                </SpotlightCard>
              {/* </div> */}
              
              {
                sessionConfig && (sessionConfig?.selectedFormat === "chat") ? (
                  <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
                    <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10 rounded-lg">
                      <QuestionDisplay
                        currentQuestion={currentQuestion}
                        questionNumber={sessionState?.currentQuestion}
                        totalQuestions={sessionState?.totalQuestions}
                        onTextToSpeech={handleTextToSpeech}
                        onNextQuestion={handleNextQuestion}
                      />
                    </div>
                  </SpotlightCard>
                ) : null
              }
              {
                sessionConfig && (sessionConfig?.selectedFormat === "chat") ? (
                  <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
                    <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10 rounded-lg">
                      <AnswerInputSection
                        mode={sessionState?.sessionMode}
                        isRecording={sessionState?.isRecording}
                        onToggleRecording={handleToggleRecording}
                        onSubmitAnswer={handleSubmitAnswer}
                        currentQuestion={currentQuestion}
                      />
                    </div>
                  </SpotlightCard>
                ): null
              }
            </div>

            {/* Right Column - Feedback (Desktop Only) */}
            {
              sessionConfig && (sessionConfig?.selectedFormat === "chat") ? (
                <div className="hidden lg:block">
                  <RealTimeFeedback
                    isVisible={showFeedback}
                    sessionData={sessionState}
                  />
                </div>
              ): null
            }
          </div>
        </div>
        </AppBackground>
      </main>
      
      {/* Session Controls */}
      <SessionControlPanel
        onPause={handlePauseSession}
        onResume={handleResumeSession}
        onSkipQuestion={handleSkipQuestion}
        onEndSession={handleEndSession}
        onOpenNotes={() => setShowNotes(!showNotes)}
        isPaused={sessionState?.isPaused}
        canSkip={sessionState?.currentQuestion < sessionState?.totalQuestions}
        sessionMode={sessionState?.sessionType}
      />
      
      {/* Notes Panel */}
      <NotesPanel
        isVisible={showNotes}
        onToggle={() => setShowNotes(!showNotes)}
        sessionId={sessionConfig?.sessionId}
      />
      
      {/* Mobile Feedback Toggle */}
      <div className="fixed top-32 right-4 z-40 lg:hidden">
        <button
          onClick={() => setShowFeedback(!showFeedback)}
          className="w-12 h-12 bg-gray-900/90 border border-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-200 backdrop-blur"
        >
          <Icon name="Activity" size={20} />
        </button>
      </div>
      
      {/* Mobile Notes Toggle */}
      <div className="fixed top-48 right-4 z-40 lg:hidden">
        <button
          onClick={() => setShowNotes(!showNotes)}
          className="w-12 h-12 bg-gray-900/90 border border-gray-700 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-200 backdrop-blur"
        >
          <Icon name="FileText" size={20} />
        </button>
      </div>
      
      {/* Mobile Real-time Feedback */}
      {showFeedback && (
        <div className="fixed bottom-24 left-4 right-4 z-40 lg:hidden">
          <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
            <div className="bg-gray-900/95 border border-gray-700 rounded-lg p-4 backdrop-blur">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-white">Live Feedback</h3>
                <button
                  onClick={() => setShowFeedback(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
              
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-lg font-bold text-green-400">85%</div>
                  <div className="text-xs text-gray-400">Confidence</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-indigo-400">Good</div>
                  <div className="text-xs text-gray-400">Eye Contact</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-yellow-400">3</div>
                  <div className="text-xs text-gray-400">Filler Words</div>
                </div>
              </div>
            </div>
          </SpotlightCard>
        </div>
      )}
      
      {/* Session Info Banner (Mobile) */}
      <div className="fixed top-16 left-0 right-0 z-30 md:hidden bg-gray-900/90 backdrop-blur border-b border-gray-700">
        <div className="px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2">
            <Icon name="Video" size={16} className="text-indigo-400" />
            <span className="text-white font-medium">{sessionConfig?.jobRole}</span>
          </div>
          <div className="text-gray-400">
            {sessionState?.currentQuestion}/{sessionState?.totalQuestions}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveInterviewSession;
